import type { ConnectionGroup, ConnectionItem, ConnectionReading } from '@solum/experience'
import type { RelationSlot } from './relations'
import type { RelatedCount } from './useRelations'

/**
 * The record page's half of the shared Connections surface (#1466).
 *
 * `useConnections` answers the DRAWER's question — *what is this record
 * connected to* — and groups per `<relType>:<reading>`, because a panel that
 * summarises a record has no use for a finer grain. The record page asks a
 * different question, and the difference is not cosmetic:
 *
 *   · the registry is the authority on what EXISTS, and a relationship with no
 *     rows yet is still a relationship you can add to. `groupConnections` drops
 *     an empty group by design (`count > 0 || items.length > 0`), so building
 *     the record page from edges alone would delete the Add for every empty
 *     relationship on it — the exact capability `RelationAdd`'s own header
 *     records nearly losing twice already;
 *   · half of a record's related lists are not edges at all. `storage='fk'`
 *     links live in a column on the child table, never in `core.relationship`,
 *     so the two reads behind `useConnections` cannot see Opportunities or
 *     Quotes. A surface built on them would silently lose both;
 *   · the unit a person states a position for, hides, pins, adds through and
 *     drills into is the SLOT (`<relType>:<direction>:<otherEntity>`). That is
 *     what `core.related_order` keys, what `relationship_frequency` counts, and
 *     what `sections[].related` declares. Lifting those to the coarser group
 *     key is lossy in both directions — `connectionArrangement` has to say "a
 *     group is pinned when ANY slot in it is pinned" precisely because the
 *     drawer has no finer place to put the answer.
 *
 * So the record page's `ConnectionGroup` IS the slot. Same type, same
 * component, same arrangement algebra; a finer key, stated by the host, which
 * is what `ConnectionGroup.key` documents.
 *
 * The edges are still reused rather than re-derived: `useConnections` has
 * already resolved every far record's title, icon and reachability through
 * `core.titles_of` (caller-RLS), so the metro lens gets governed names here for
 * free instead of a second identity resolution.
 */

/** The reading a slot has of its relationship, including governed symmetry. */
export function slotReading(slot: RelationSlot): ConnectionReading {
  return slot.direction
}

/**
 * The edges that belong to one slot.
 *
 * A SYMMETRIC self-referential slot (`direction: 'both'`) is stored on
 * whichever end the person who made the link happened to use, so it collects
 * both readings — the same rule `useRelationRows` follows for the grid, and the
 * `key` dedupe is what stops one stored edge from appearing twice.
 */
function itemsOf(slot: RelationSlot, groups: readonly ConnectionGroup[]): ConnectionItem[] {
  const readings: ConnectionReading[] = [slotReading(slot)]
  const seen = new Set<string>()
  const out: ConnectionItem[] = []
  for (const reading of readings) {
    for (const group of groups) {
      if (group.relType !== slot.relType || group.reading !== reading) continue
      for (const item of group.items) {
        if (item.entity !== slot.otherEntity || seen.has(item.key)) continue
        seen.add(item.key)
        out.push(item)
      }
    }
  }
  return out
}

/**
 * One `ConnectionGroup` per registered slot, in the order the caller hands the
 * slots over — `orderSlots` has already applied *stated → populated → this
 * person's frequency → alphabetical*, and `arrangeConnections` preserves it.
 *
 * ── the count is the SERVER's, or it is NOTHING (#2131) ────────────────────
 *
 * This used to read `counted?.n ?? items.length`, described as falling back to
 * the loaded edges "where the door said nothing". Both halves of that fallback
 * are wrong, and the second is the release defect:
 *
 *   · this function builds a group for EVERY REGISTERED SLOT, including slots
 *     with no count row and no loaded page. `items.length` is then `0`, so a
 *     relationship the server never counted — or refused to count — arrived at
 *     the shared contract as one MEASURED empty. `isGroupEmpty` exists because
 *     of exactly that collapse, and `slotNeedsRows` below already refuses to
 *     make it: it reads an uncountable slot precisely because "collapsing it
 *     would present an unknown as an absence";
 *   · even where rows ARE loaded, they are a page. `useRelationRows` reads one,
 *     so `items.length` is what this screen has, not what exists. Publishing it
 *     as the total invents an exact number out of a truncation.
 *
 * So the count is `counted?.n`, and `null` when the door said nothing. Unknown
 * travels; the renderers say so; nothing downstream is allowed to guess.
 */
export function slotConnectionGroups(
  slots: readonly RelationSlot[],
  connections: readonly ConnectionGroup[],
  countOf: (slotId: string) => RelatedCount | undefined,
  titleOf?: (slotId: string) => string | undefined,
  /**
   * The far entity's governed glyph — `core.entity_identity.icon_key` (#2349 §D2).
   *
   * A RESOLVER rather than a value, and optional: the registry is a query that
   * may not have answered yet, and a header that drew a stand-in glyph while it
   * loaded would be inventing an identity. Absent, or answering null, renders no
   * icon at all.
   */
  iconOf?: (entity: string) => string | null | undefined,
): ConnectionGroup[] {
  return slots.map((slot) => {
    const items = itemsOf(slot, connections)
    const counted = countOf(slot.id)
    // `?? null`, never `?? items.length` — see the header. The mutation that
    // restores the old expression is the one `recordConnections.test.ts` names.
    const count = counted?.n ?? null
    return {
      key: slot.id,
      relType: slot.relType,
      reading: slotReading(slot),
      // The form definition's title wins where it declared one (#1466), because
      // a definition that names a list and is then overruled by the registry's
      // default is a governed statement nobody can see the effect of.
      label: titleOf?.(slot.id) ?? slot.label,
      /* `?? null` and never a fallback glyph: the registry not having answered
         and the entity not declaring one are both "no icon", and both are
         truthful. `CircleDot` beside a name would be this surface stating an
         identity nothing governs. */
      iconKey: iconOf?.(slot.otherEntity) ?? null,
      items,
      byType: [{ entity: slot.otherEntity, label: slot.otherLabel, count }],
      count,
      capped: counted?.capped ?? false,
    }
  })
}

/**
 * Does this slot need its rows read?
 *
 * A slot the server counted as zero is empty, and reading it would be one round
 * trip per empty relationship — nine on the fixture's sparsest organisation — to
 * be told what the counts call already said. An UNCOUNTABLE slot (`undefined`:
 * the door derived no count, or the read was refused) is read, for the reason
 * the cascade already gave: collapsing it would present an unknown as an
 * absence.
 */
export function slotNeedsRows(counted: RelatedCount | undefined): boolean {
  return counted === undefined || counted.n > 0
}

/**
 * Is this relationship group worth space in the record workspace?
 *
 * Only an exact server answer of zero, with no already-loaded witness to the
 * contrary, is absence. `null` is deliberately visible: it means the count
 * door did not measure this slot, not that the slot is empty. Where the Metro
 * read supplies loaded edge items, those also win over a stale zero. Grid and
 * Cards do not infer that witness: their slot rows mount only after this gate.
 */
export function relationshipGroupIsVisible(group: ConnectionGroup): boolean {
  return group.count === null || group.count > 0 || group.items.length > 0
}

/** Preserve the canonical order handed in; this predicate never re-sorts. */
export function visibleRelationshipGroups(groups: readonly ConnectionGroup[]): ConnectionGroup[] {
  return groups.filter(relationshipGroupIsVisible)
}

/** An authored section with no visible group must not leave an orphan heading. */
export function visibleRelationshipSections<T extends { groups: readonly ConnectionGroup[] }>(
  sections: readonly T[],
): T[] {
  return sections.filter((section) => section.groups.length > 0)
}
